import { Link } from '@/lib/router';
import {
  LegalPageLayout,
  LegalSection as Section,
  type LegalSectionLink,
} from '@/components/public/LegalPageStructure';

const LAST_UPDATED = {
  dateTime: '2026-05-20',
  display: 'May 20, 2026',
} as const;
const EFFECTIVE = {
  dateTime: '2026-05-20',
  display: 'May 20, 2026',
} as const;

const TERMS_SECTIONS = [
  { id: 'what-prometheus-is', label: 'What Prometheus is' },
  {
    id: 'not-legal-advice',
    label: 'Not legal advice; no attorney–client relationship',
  },
  {
    id: 'accuracy-currency-verification',
    label: 'Accuracy, currency, and verification',
  },
  { id: 'accounts-access', label: 'Accounts and access' },
  { id: 'acceptable-use', label: 'Acceptable use' },
  {
    id: 'corpus-intellectual-property',
    label: 'The corpus and intellectual property',
  },
  { id: 'third-party-services', label: 'Third-party services' },
  { id: 'warranty-disclaimer', label: 'Disclaimer of warranties' },
  { id: 'limitation-liability', label: 'Limitation of liability' },
  { id: 'indemnification', label: 'Indemnification' },
  { id: 'fees-subscriptions', label: 'Fees and paid subscriptions' },
  { id: 'suspension-termination', label: 'Suspension and termination' },
  { id: 'changes', label: 'Changes to these Terms' },
  { id: 'governing-law', label: 'Governing law' },
  { id: 'contact', label: 'Contact' },
] as const satisfies readonly LegalSectionLink[];

export function Terms() {
  return (
    <LegalPageLayout
      page="terms"
      title="Terms of Service"
      dates={[
        { label: 'Effective', ...EFFECTIVE },
        { label: 'Last updated', ...LAST_UPDATED },
      ]}
      sections={TERMS_SECTIONS}
      intro={
        <p>
            These Terms of Service ("Terms") govern your access to and use of
            Prometheus Legal Intelligence ("Prometheus," "we," "us"), a Model
            Context Protocol (MCP) connector operated by Promethean
            Intelligence Group LLC that surfaces primary American law to AI
            assistants. By connecting Prometheus to Claude or otherwise using
            the service, you agree to these Terms. If you do not agree, do
            not use the service.
        </p>
      }
    >
          <Section id="what-prometheus-is" n={1} title="What Prometheus is">
            <p>
              Prometheus is a read-only research connector. It returns
              passages, citations, and metadata drawn from public-record
              primary law (federal and state caselaw, the U.S. Code, the Code
              of Federal Regulations, federal rules, the MPEP, and related
              sources) in response to tool calls from an AI assistant. It
              does not draft, file, send, transact, or take any action on
              your behalf &mdash; every tool is read-only.
            </p>
          </Section>

          <Section
            id="not-legal-advice"
            n={2}
            title="Not legal advice; no attorney–client relationship"
          >
            <p>
              Prometheus is a legal-research tool, not a law firm, and using
              it does not create an attorney&ndash;client relationship.
              Nothing returned by Prometheus is legal advice. Outputs are
              research aids that must be independently verified by a
              qualified professional before being relied upon for any legal
              matter. You are solely responsible for how you use the
              results, including any reliance, citation, or filing.
            </p>
          </Section>

          <Section
            id="accuracy-currency-verification"
            n={3}
            title="Accuracy, currency, and your responsibility to verify"
          >
            <p>
              We work to ground every result in a verifiable public-record
              source, but Prometheus is research-grade, not
              citation-of-record-grade. The corpus is a periodically
              refreshed snapshot; recent opinions, amendments, and citation
              history may not yet be reflected, and source extraction is
              occasionally lossy. We do not warrant that any result is
              complete, current, or free of error. For matter-critical work,
              confirm every authority against the official source and a
              citator (e.g., KeyCite/Shepard's). See the{' '}
              <Link to="/docs">documentation</Link> for known limits and
              gaps.
            </p>
          </Section>

          <Section id="accounts-access" n={4} title="Accounts and access">
            <p>
              Connecting Prometheus to Claude provisions an account
              automatically through an OAuth 2.1 (PKCE) consent flow; we
              receive a stable hashed identifier and the email associated
              with your authorization (see the{' '}
              <Link to="/privacy">Privacy Policy</Link>). You must be at
              least 18 and able to form a binding contract. You are
              responsible for activity under your account. The public-record
              corpus is available on a free tier; practitioner verticals
              require a paid subscription.
            </p>
          </Section>

          <Section id="acceptable-use" n={5} title="Acceptable use">
            <p>You agree not to:</p>
            <ul>
              <li>
                exceed, evade, or automate around published rate limits;
              </li>
              <li>
                bulk-export, scrape, mirror, or otherwise systematically
                copy the corpus or Prometheus's structured metadata to build
                or train a competing dataset, model, or product;
              </li>
              <li>
                resell or redistribute access without our written
                permission;
              </li>
              <li>
                probe, attack, or attempt to gain unauthorized access to
                the service or its infrastructure; or
              </li>
              <li>
                use the service in violation of law or the rights of
                others.
              </li>
            </ul>
            <p>
              We may rate-limit, suspend, or terminate access for conduct
              that threatens the service's reliability or integrity.
            </p>
          </Section>

          <Section
            id="corpus-intellectual-property"
            n={6}
            title="The corpus and intellectual property"
          >
            <p>
              The primary law Prometheus returns is public-record material
              that is not subject to copyright &mdash; federal statutes,
              regulations, court opinions, and government publications are
              in the public domain (17 U.S.C. § 105 and analogous
              principles), and state statute text is public domain per{' '}
              <em>
                Veeck v. Southern Building Code Congress, Inc.
              </em>
              , 293 F.3d 791 (5th Cir. 2002). Prometheus's own contributions
              &mdash; its structured extractions, holding summaries,
              doctrine tags, citation graph, search metadata, software, and
              the arrangement of the corpus &mdash; are the property of
              Promethean Intelligence Group LLC. Subject to these Terms, we
              grant you a limited, non-exclusive, non-transferable license
              to use Prometheus's outputs for your own legal-research
              purposes; this license does not include the rights restricted
              in Section 5.
            </p>
          </Section>

          <Section id="third-party-services" n={7} title="Third-party services">
            <p>
              Prometheus relies on third-party infrastructure and model
              providers (currently OpenAI for query-time embeddings,
              Supabase for database and edge-function hosting, and
              Anthropic as the assistant that calls the connector). Your
              use of those services through Prometheus is also subject to
              their terms. How data is processed across these services is
              described in our <Link to="/privacy">Privacy Policy</Link>.
            </p>
          </Section>

          <Section id="warranty-disclaimer" n={8} title="Disclaimer of warranties">
            <p>
              The service is provided "as is" and "as available," without
              warranties of any kind, whether express, implied, or
              statutory, including any implied warranties of
              merchantability, fitness for a particular purpose, accuracy,
              or non-infringement. We do not warrant that the service will
              be uninterrupted, error-free, or that any result is accurate,
              complete, or current.
            </p>
          </Section>

          <Section id="limitation-liability" n={9} title="Limitation of liability">
            <p>
              To the fullest extent permitted by law, Promethean
              Intelligence Group LLC will not be liable for any indirect,
              incidental, special, consequential, or punitive damages, or
              for any loss of profits, data, goodwill, or for damages
              arising from your reliance on any result, even if advised of
              the possibility. Our total aggregate liability for any claim
              relating to the service will not exceed the greater of the
              amount you paid us for the service in the twelve months
              before the claim or one hundred U.S. dollars (US$100).
            </p>
          </Section>

          <Section id="indemnification" n={10} title="Indemnification">
            <p>
              You agree to indemnify and hold harmless Promethean
              Intelligence Group LLC from any claims, losses, or expenses
              (including reasonable attorneys' fees) arising out of your
              use of the service, your violation of these Terms, or your
              violation of any law or third-party right.
            </p>
          </Section>

          <Section
            id="fees-subscriptions"
            n={11}
            title="Fees and paid subscriptions"
          >
            <p>
              The public-record corpus is free. Practitioner verticals are
              offered on a paid subscription (as described at the time of
              purchase). Fees are billed in advance and are non-refundable
              except where required by law. You may cancel at any time;
              cancellation stops future billing and takes effect at the
              end of the current billing period. We may change pricing
              prospectively with notice.
            </p>
          </Section>

          <Section
            id="suspension-termination"
            n={12}
            title="Suspension and termination"
          >
            <p>
              You may stop using Prometheus at any time by disconnecting
              it from Claude. We may suspend or terminate access if you
              violate these Terms or to protect the service or other
              users. You may request deletion of your account and
              associated records as described in the{' '}
              <Link to="/privacy">Privacy Policy</Link>. Sections that by
              their nature should survive termination (including Sections
              6, 8, 9, and 10) survive.
            </p>
          </Section>

          <Section id="changes" n={13} title="Changes to these Terms">
            <p>
              We may update these Terms from time to time. If we make
              material changes, we will update the "Last updated" date
              above and, where meaningful, notify connected users through
              Claude's normal connector-update flow. Your continued use
              after changes take effect constitutes acceptance.
            </p>
          </Section>

          <Section id="governing-law" n={14} title="Governing law">
            <p>
              These Terms are governed by the laws of the State of Texas,
              without regard to its conflict-of-laws rules, and any dispute
              will be brought in the state or federal courts located in
              Texas.
            </p>
          </Section>

          <Section id="contact" n={15} title="Contact">
            <p>
              Questions about these Terms:{' '}
              <a href="mailto:adrian@prometheuslaw.app">
                adrian@prometheuslaw.app
              </a>
              .
            </p>
          </Section>
    </LegalPageLayout>
  );
}
